import {
  EntryTypeEnum,
  IConfigOfferEntryDto,
  ILoyaltyPromotionInput,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  LoyaltyPromotionType,
  ProductTypeDto,
} from 'generated/graphql-gateway';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { ICartEntryAdapter } from 'state/loyalty/hooks/types';
import { isReward } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { logger } from 'utils/logger';

import { ICartEntry, MinimalCartEntryType } from '../types';

export type LookUpContext = {
  indexedMenu: IndexedMenu<ILocalizedMenuEntry>;
  indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto>;
  indexedRewards: IndexedMenu<IRewardEntryDto>;
};

/**
 * Checks if the cart entries have any offers
 * @param cartEntries - The cart entries to check
 * @returns True if the cart entries have any offers, false otherwise
 */
export const hasOffers = (cartEntries: ICartEntry[]) => {
  return cartEntries.some(entry => isOfferCartEntry(entry));
};

export const hasRewards = (cartEntries: ICartEntry[]) => {
  return cartEntries.some(entry => isReward(entry));
};

export const isOfferCartEntry = (cartEntry: ICartEntry) => {
  return (
    cartEntry.type === MinimalCartEntryType.discount ||
    cartEntry.type === MinimalCartEntryType.incentive
  );
};

/** Incentive offer line only (excludes percentage/amount discount rows). */
export const isIncentiveOfferCartEntry = (cartEntry: ICartEntry) => {
  return cartEntry.type === MinimalCartEntryType.incentive;
};

export const isRewardCartEntry = (cartEntry: ICartEntry) => {
  return cartEntry.type === MinimalCartEntryType.reward;
};

export const isIncentiveOrCombo = (cartEntry: ICartEntry) => {
  return (
    cartEntry.type === MinimalCartEntryType.combo ||
    cartEntry.type === MinimalCartEntryType.incentive
  );
};

export const isItemOrCombo = (cartEntry: ICartEntry) => {
  return (
    cartEntry.type === MinimalCartEntryType.item || cartEntry.type === MinimalCartEntryType.combo
  );
};

/** True when the cart has at least one line that should keep the user on the cart screen.
 *  Discount rows alone do not qualify. */
export const cartHasQualifyingRedirectContent = (cartEntries: ICartEntry[]): boolean => {
  return cartEntries.some(
    entry => isItemOrCombo(entry) || isRewardCartEntry(entry) || isIncentiveOfferCartEntry(entry)
  );
};

/**
 * Determines the minimal cart entry type for a given entry ID by checking
 * various indexed collections in order of priority.
 *
 * Priority order:
 * 1. Donations
 * 2. Extras
 * 3. Rewards
 * 4. Offers (discount vs incentive)
 * 5. Menu entries (by type)
 */
export const getMinimalCartEntryType = (
  entryId: string,
  lookUpContext: LookUpContext
): MinimalCartEntryType => {
  const { indexedRewards, indexedOffers, indexedMenu } = lookUpContext;

  if (indexedRewards.find(entryId)) {
    return MinimalCartEntryType.reward;
  }

  const offerEntry = indexedOffers.find(entryId);
  if (offerEntry) {
    // Check if discountType is 'percentage' or 'amount' for discount, otherwise incentive
    return offerEntry.discountType === 'PERCENTAGE' || offerEntry.discountType === 'AMOUNT'
      ? MinimalCartEntryType.discount
      : MinimalCartEntryType.incentive;
  }

  const menuEntry = indexedMenu.find(entryId);
  return mapEntryTypeToMinimalCartEntryType(menuEntry?.type, menuEntry?.productType);
};

/**
 * Maps menu entry types to their corresponding minimal cart entry types.
 * Defaults to 'item' for unknown or missing types.
 */
const mapEntryTypeToMinimalCartEntryType = (
  entryType?: EntryTypeEnum,
  productType?: readonly ProductTypeDto[] | null
): MinimalCartEntryType => {
  switch (entryType) {
    case EntryTypeEnum.ITEM:
      if ((productType ?? []).includes(ProductTypeDto.NON_FOOD)) {
        // there are multiple items marked as non-food, at this point we are assuming
        // that anything we add to the cart that is non-food is either a donation or an extra
        return (productType ?? []).includes(ProductTypeDto.DONATIONS)
          ? MinimalCartEntryType.donation
          : MinimalCartEntryType.extra;
      }
      return MinimalCartEntryType.item;
    case EntryTypeEnum.COMBO:
      return MinimalCartEntryType.combo;
    case EntryTypeEnum.MODIFIER:
      return MinimalCartEntryType.modifier;
    default:
      return MinimalCartEntryType.item;
  }
};

/**
 * recursively maps the cart entries to the loyalty cart entries
 * @param cartEntries - The cart entries to map
 * @returns The loyalty cart entries
 */
export const mapCartEntriesToLoyaltyCartEntries = (
  cartEntry: ICartEntry[],
  incentiveType: LoyaltyPromotionType
): ICartEntryAdapter[] => {
  return cartEntry.map(entry => ({
    sanityId: entry.id,
    lineId: entry.cartId ?? '',
    price: entry.price?.amount ?? 0,
    quantity: entry.quantity ?? 1,
    children: mapCartEntriesToLoyaltyCartEntries(entry.children, incentiveType),
  }));
};

export const getLoyaltyAppliedIncentives = ({
  cartEntries,
  incentiveType,
}: {
  cartEntries: ICartEntry[];
  incentiveType: LoyaltyPromotionType;
}): ILoyaltyPromotionInput[] => {
  return cartEntries
    .filter(entry =>
      incentiveType === LoyaltyPromotionType.OFFER
        ? isOfferCartEntry(entry)
        : isRewardCartEntry(entry)
    )
    .map(entry => ({
      id: entry.referenceId ?? '', // in this case is the loyaltyEngineId
      lineId: entry.cartId ?? '',
      type: incentiveType,
    }));
};

/**
 * Calculates the price of a cart entry's children
 * @param entry - The cart entry to calculate the children price for
 * @returns The price of the children
 */
export const calculateEntryChildrenPrice = (entry: ICartEntry): number => {
  return (entry.children ?? []).reduce((acc, child) => acc + calculateEntryTotalPrice(child), 0);
};

/**
 * Calculates the total price of a cart entry including all nested children
 */
export const calculateEntryTotalPrice = (entry: ICartEntry): number => {
  const entryPrice = (entry.price?.amount ?? 0) * (entry.quantity ?? 1);
  const childrenPrice = (entry.children ?? []).reduce(
    (acc, child) => acc + calculateEntryTotalPrice(child),
    0
  );
  return entryPrice + childrenPrice;
};

/**
 * Calculates the subtotal of a cart entries excluding the donation items
 * as they are not included in the subtotal calculation
 */
export const calculateCartSubTotal = (cartEntries: ICartEntry[]): number => {
  const subTotal = cartEntries.reduce((acc, entry) => acc + calculateEntryTotalPrice(entry), 0);

  if (subTotal < 0) {
    logger.warn({
      message: '[calculateCartSubTotal]: Subtotal is negative',
      context: { cartEntries, subTotal },
    });
    return 0;
  }

  return subTotal;
};

export type EstimatedDeliveryFees = {
  /** Delivery fee after any local discounts (e.g. free delivery threshold). */
  deliveryFeeCents: number;
  /** Delivery fee discount applied locally (informational). */
  deliveryFeeDiscountCents: number;
  /** Service fee computed as a percent of subtotal. */
  serviceFeeCents: number;
  /** Small cart fee applied when subtotal is under threshold. */
  smallCartFeeCents: number;
  /** Additional geographically-required fee (e.g. CO retail delivery fee). */
  geographicalFeeCents: number;
  /** Convenience sum of all fees above. */
  totalFeesCents: number;
};

/**
 * Local fee estimator for cart-level display (pre-pricing).
 *
 * Important: Backend pricing is the source of truth. This is intended only as a
 * best-effort estimate to show users what to expect before checkout pricing runs.
 *
 * Rules:
 * - All states except CA:
 *   - Delivery Fee: $2.99 ($0 for orders $25+)
 *   - Service Fee: $0 (not applied)
 *   - Small Cart Fee: $2.50 on orders under $10
 * - CO:
 *   - Delivery Fee: $2.99 ($0 for orders $25+)
 *   - Service Fee: 15% of order subtotal
 *   - Small Cart Fee: $2.50 on orders under $10
 *   - Colorado Retail Delivery Fee: $0.29
 * - CA:
 *   - Delivery Fee: $3.00 (no free delivery threshold per provided rules)
 *   - Service Fee: 15% of order subtotal
 *   - Small Cart Fee: $2.50 on orders under $10
 */
export const estimateDeliveryFees = ({
  subTotalCents,
  dropoffState,
}: {
  subTotalCents: number;
  dropoffState?: string | null;
}): EstimatedDeliveryFees => {
  const state = (dropoffState ?? '').toUpperCase();
  const safeSubTotalCents = Math.max(subTotalCents, 0);

  const SERVICE_FEE_PERCENT = 0.15;
  const SMALL_CART_THRESHOLD_CENTS = 1000; // $10
  const SMALL_CART_FEE_CENTS = 250; // $2.50
  const FREE_DELIVERY_THRESHOLD_CENTS = 2500; // $25

  const isCA = state === 'CA';
  const isCO = state === 'CO';

  const baseDeliveryFeeCents = isCA ? 300 : 299;
  const qualifiesFreeDelivery = !isCA && safeSubTotalCents >= FREE_DELIVERY_THRESHOLD_CENTS;

  const deliveryFeeDiscountCents = qualifiesFreeDelivery ? baseDeliveryFeeCents : 0;
  const deliveryFeeCents = Math.max(baseDeliveryFeeCents - deliveryFeeDiscountCents, 0);

  const hasServiceFee = isCA || isCO;
  const serviceFeeCents = hasServiceFee
    ? Math.max(Math.round(safeSubTotalCents * SERVICE_FEE_PERCENT), 0)
    : 0;
  const smallCartFeeCents =
    safeSubTotalCents < SMALL_CART_THRESHOLD_CENTS ? SMALL_CART_FEE_CENTS : 0;

  // CO retail delivery fee (required by law).
  const geographicalFeeCents = isCO ? 29 : 0;

  const totalFeesCents =
    deliveryFeeCents + serviceFeeCents + smallCartFeeCents + geographicalFeeCents;

  return {
    deliveryFeeCents,
    deliveryFeeDiscountCents,
    serviceFeeCents,
    smallCartFeeCents,
    geographicalFeeCents,
    totalFeesCents,
  };
};

/**
 * Cart "subtotal" adjusted to include estimated delivery fees for delivery orders.
 * Intended for cart-level display only (pre-pricing).
 */
export const calculateCartSubTotalWithEstimatedFees = ({
  cartEntries,
  isDelivery,
  dropoffState,
}: {
  cartEntries: ICartEntry[];
  isDelivery: boolean;
  dropoffState?: string | null;
}): number => {
  const subTotalCents = calculateCartSubTotal(cartEntries);
  if (!isDelivery) {
    return subTotalCents;
  }

  const fees = estimateDeliveryFees({ subTotalCents, dropoffState });
  return subTotalCents + fees.totalFeesCents;
};

export const countCartItems = (cartEntries: ICartEntry[]) => {
  return cartEntries.reduce((total, cartEntry) => {
    if (
      cartEntry.type === MinimalCartEntryType.discount ||
      cartEntry.cartId === DISCOUNT_OFFER_CART_ID ||
      cartEntry.type === MinimalCartEntryType.donation ||
      cartEntry.type === MinimalCartEntryType.extra
    ) {
      return total;
    }
    return total + cartEntry.quantity;
  }, 0);
};

export const getRewardBenefitId = (cartEntry: ICartEntry): string | undefined => {
  if (cartEntry.type === MinimalCartEntryType.reward) {
    return cartEntry.children?.[0]?.id;
  }
  return undefined;
};
